import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { intakeQuestionnaireActions } from '../../../../../../../store';
import useIntakeTherapyPageState from './useIntakeTherapyPageState';

import type { AppDispatch } from '../../../../../../../store';
import type { TherapyPayload } from '../../../../../../common';

export default function useIntakeTherapies() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { intakeQuestionnaireId } = useParams<'intakeQuestionnaireId'>();
  const { therapies, hasNoTherapies, isSaving, isBulkSaving } = useIntakeTherapyPageState();

  const create = useCallback(
    async (data: TherapyPayload) => {
      if (!intakeQuestionnaireId) return undefined;
      const result = await dispatch(
        intakeQuestionnaireActions.createIntakeTherapy({
          intakeQuestionnaireId,
          data,
          getAccessTokenSilently,
        }),
      );
      return intakeQuestionnaireActions.createIntakeTherapy.fulfilled.match(result)
        ? result.payload.id
        : undefined;
    },
    [dispatch, getAccessTokenSilently, intakeQuestionnaireId],
  );

  const update = useCallback(
    async (therapyId: number, data: TherapyPayload) => {
      if (!intakeQuestionnaireId) return false;
      const result = await dispatch(
        intakeQuestionnaireActions.updateIntakeTherapy({
          intakeQuestionnaireId,
          therapyId,
          data,
          getAccessTokenSilently,
        }),
      );
      return intakeQuestionnaireActions.updateIntakeTherapy.fulfilled.match(result);
    },
    [dispatch, getAccessTokenSilently, intakeQuestionnaireId],
  );

  const remove = useCallback(
    async (therapyId: number) => {
      if (!intakeQuestionnaireId) return false;
      const result = await dispatch(
        intakeQuestionnaireActions.deleteIntakeTherapy({
          intakeQuestionnaireId,
          therapyId,
          getAccessTokenSilently,
        }),
      );
      return intakeQuestionnaireActions.deleteIntakeTherapy.fulfilled.match(result);
    },
    [dispatch, getAccessTokenSilently, intakeQuestionnaireId],
  );

  const clearAll = useCallback(async () => {
    if (!intakeQuestionnaireId) return false;
    const result = await dispatch(
      intakeQuestionnaireActions.deleteAllIntakeTherapies({
        intakeQuestionnaireId,
        getAccessTokenSilently,
      }),
    );
    return intakeQuestionnaireActions.deleteAllIntakeTherapies.fulfilled.match(result);
  }, [dispatch, getAccessTokenSilently, intakeQuestionnaireId]);

  const setNoTherapies = useCallback(
    async (hasNoTherapies: boolean) => {
      if (!intakeQuestionnaireId) return false;
      const result = await dispatch(
        intakeQuestionnaireActions.setIntakeHasNoTherapies({
          intakeQuestionnaireId,
          hasNoTherapies,
          getAccessTokenSilently,
        }),
      );
      return intakeQuestionnaireActions.setIntakeHasNoTherapies.fulfilled.match(result);
    },
    [dispatch, getAccessTokenSilently, intakeQuestionnaireId],
  );

  return {
    therapies,
    hasNoTherapies,
    isSaving,
    isBulkSaving,
    create,
    update,
    remove,
    clearAll,
    setNoTherapies,
  };
}
